import type { ScreenerFilter } from '@api';
import { Icon } from '@design/components/icon/Icon';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import * as Radix from '@radix-ui/react-dropdown-menu';
import { FilterResetButton } from '@terminal/components/filters/components/filter-reset-button/FilterResetButton';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import {
  FilterOption,
  FilterUseType,
  ToggleFilterProps,
} from '@terminal/components/update-filter-options/updateFilterOptions';
import {
  FilterGroupKeys,
  UseFilterActionsReturn,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { FilterSearch } from '@terminal/views/screener/filter-search/FilterSearch';
import { useFilterOptionSearch } from '@terminal/views/screener/use-filter-option-search/useFilterOptionSearch';
import { isFilterWithChildOptionActive } from '@terminal/views/screener/use-screener-store/filter-utils';
import clsx from 'clsx';
import React, { ReactNode, useState } from 'react';

import { ActiveFilterOptions } from '../filters/Filters';
import { ChildFilter } from './ChildFilter';
import styles from './Filter.module.scss';
import { FilterFooterActions } from './FilterFooterActions';
import { FilterLabel } from './FilterLabel';
import type {
  FilterVariant,
  IncludeExcludeLabels,
} from './include-exclude/include-exclude.types';
import {
  getDefaultFilterOption,
  getFilterOptionsBySelectionMode,
} from './include-exclude/utils/include-exclude.utils';
import { MultiSelectedOptions } from './MultiSelectedOptions';
import { Option } from './Option';

interface FilterWithIsdisabledProp extends ScreenerFilter {
  isDisabled?: boolean;
}

export interface FilterRenderOptionsProps {
  options: FilterOption[];
  showEmpty: boolean;
}

export type FilterProps<T extends FilterGroupKeys = FilterGroupKeys> = {
  filter: FilterWithIsdisabledProp;
  labels: {
    searchPlaceholder: string;
    resetLabel: string;
    noResultsLabel: string;
    selectedLabel: string;
    disabledTooltipLabel: string;
    multiselectFeatured: string;
    remove: string;
    setCustomRange: string;
    includeExcludeLabels?: IncludeExcludeLabels;
    renderCustomFilterOptionLabel?: (filterOption: FilterOption) => ReactNode;
  };
  useType?: FilterUseType;
  multiselect?: boolean;
  showTriggerLabel?: boolean;
  filterOptions: ActiveFilterOptions['options'];
  filterOptionGroups?: ActiveFilterOptions[];
  disabledOptions?: ActiveFilterOptions['options'];
  addFilterOption: UseFilterActionsReturn<T>['addFilterOption'];
  removeFilterOption: UseFilterActionsReturn<T>['removeFilterOption'];
  resetFilterOptions?: UseFilterActionsReturn<T>['resetFilterOptions'];
  onRemove?: UseFilterActionsReturn<T>['removeFilter'];
  onCustomRangeClick: () => void;
  id?: string;
  showListTitle?: boolean;
  hideResetButton?: boolean;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  variant?: FilterVariant;
  renderOptions?: (props: FilterRenderOptionsProps) => ReactNode;
};

const getCustomHeaderColorClassName = (
  color: FilterOption['customHeaderColor']
) => {
  switch (color) {
    case 'indigo':
      return styles.customHeaderIndigo;
    case 'purple':
      return styles.customHeaderPurple;
    case 'teal':
      return styles.customHeaderTeal;
    default:
      return undefined;
  }
};

export const Filter = <T extends FilterGroupKeys>({
  filter,
  id = filter.key,
  labels,
  filterOptions,
  filterOptionGroups = [],
  disabledOptions,
  removeFilterOption,
  addFilterOption,
  resetFilterOptions,
  showTriggerLabel = true,
  useType = 'single-use',
  multiselect = useType === 'single-use' && filter.config.multiselect,
  onRemove,
  onCustomRangeClick,
  showListTitle = false,
  hideResetButton = false,
  contentClassName,
  contentStyle,
  variant = 'default',
  renderOptions,
}: FilterProps<T>) => {
  const {
    name,
    tooltip,
    searchable,
    config,
    key: filterKey,
    dynamic,
    isDisabled = false,
  } = filter;
  const [open, setOpen] = useState(false);
  const isIncludeExcludeFilter = variant !== 'default';
  const isSingleSelectIncludeExcludeFilter =
    variant === 'include-exclude-single-select';
  const { includedOptions, excludedOptions } =
    getFilterOptionsBySelectionMode(filterOptionGroups);

  const handleReset = resetFilterOptions
    ? (e: Event) => {
        e.preventDefault();
        resetFilterOptions(filterKey);
      }
    : undefined;

  const {
    data: searchData,
    handleChange,
    value,
    onClear,
  } = useFilterOptionSearch({
    filter: filterKey,
    defaultOptions: config.options,
  });

  const data = searchable ? searchData : config.options;
  const defaultOption = getDefaultFilterOption(config.options, config.default);

  const onFilterSelect = (props: ToggleFilterProps, active: boolean) => {
    if (active) {
      removeFilterOption(props);

      if (
        multiselect &&
        config.min_select &&
        config.default &&
        filterOptions.length === 1
      ) {
        if (defaultOption) {
          addFilterOption({
            filterKey,
            multiselect,
            option: defaultOption,
          });
        }
      }
    } else {
      addFilterOption(props);
    }

    if (useType === 'multi-use') {
      onClear();
    }
  };

  const isChecked = (optKey: string) =>
    filterOptions.some(f => f.key === optKey);

  const handleOutsideClick = () => {
    const onlyChildrenSelected =
      !!filterOptions.length &&
      filterOptions.filter(o => o.childName).length === filterOptions.length;

    if (onlyChildrenSelected) {
      removeFilterOption({
        filterKey,
        option: filterOptions[0],
      });
    }

    onClear();
  };

  const handleRemove = () => {
    onRemove?.({
      filterKey: filter.key,
      filterId: id,
      option: filterOptions[0],
      ...(isSingleSelectIncludeExcludeFilter
        ? { selectionMode: filterOptionGroups[0]?.selectionMode }
        : {}),
    });
  };

  const handleOpen = () => setOpen(true);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen && useType === 'multi-use') {
      onClear();
    }
  };

  const filteredOptions = data.map(option => {
    const checked = isChecked(option.key);
    const customHeaderTitle = option?.customHeaderTitle;
    const customHeaderColor = option?.customHeaderColor;
    const hasBottomBorder = option?.hasBottomBorder;

    if (multiselect && checked && !value) {
      return null;
    }

    const disabled = !!disabledOptions?.some(o => o.key === option.key);

    return (
      <div key={option.key}>
        {customHeaderTitle && (
          <div
            className={clsx(
              styles.customHeaderLabel,
              getCustomHeaderColorClassName(customHeaderColor)
            )}
          >
            {customHeaderTitle}
          </div>
        )}
        <Option
          key={option.key}
          option={option}
          disabled={disabled}
          isChecked={checked && !disabled}
          handleClick={() =>
            onFilterSelect(
              {
                option,
                filterKey,
                filterId: id,
                multiselect,
                dynamic,
              },
              checked
            )
          }
          multiselect={multiselect}
          renderCustomFilterOptionLabel={labels.renderCustomFilterOptionLabel}
        />
        {hasBottomBorder && <div className={styles.customBottomBorder} />}
      </div>
    );
  });

  const showEmpty = !filteredOptions.length && value;
  const isParentOptionActive = isFilterWithChildOptionActive(filterOptions);
  const showResetButton =
    !hideResetButton && handleReset && !value && isParentOptionActive;
  const isFilterActive = useType === 'single-use' && isParentOptionActive;
  const isMultiUseSingleFilter = useType === 'single-use' && id !== filter.key;
  const listTitle = searchable ? `${labels.multiselectFeatured}${name}` : name;
  //hide list if there is a search value OR multiselect + ALL items are selected
  const displayList =
    !value && (!multiselect || filterOptions.length !== data.length);

  return (
    <Radix.Root open={open} onOpenChange={handleOpenChange}>
      <TooltipContainer label={tooltip} trigger={[TooltipTrigger.Hover]}>
        <Radix.Trigger
          className={isFilterActive ? styles.triggerActive : styles.trigger}
          data-filter={id}
          data-testid={`${useType}-filter`}
          onClick={handleOpen}
          disabled={isDisabled}
        >
          {(showTriggerLabel || !isFilterActive) && (
            <span
              className={styles.triggerLabel}
              data-trigger-label
              data-testid="filter-name"
            >
              {name}
            </span>
          )}
          {isFilterActive && (
            <FilterLabel
              options={filterOptions}
              multiselect={multiselect}
              includedOptions={includedOptions}
              excludedOptions={excludedOptions}
              includeExcludeLabels={labels.includeExcludeLabels}
              variant={variant}
            />
          )}
          <Icon
            iconName="ChevronLightDown"
            size={14}
            fillColor="var(--icon-soft)"
          />
        </Radix.Trigger>
      </TooltipContainer>
      <Radix.Portal>
        <Radix.Content
          className={clsx(styles.dropdownMenuContent, contentClassName, {
            [styles.includeExcludeFilterContent]: isIncludeExcludeFilter,
          })}
          align="start"
          sideOffset={5}
          collisionPadding={5}
          onInteractOutside={handleOutsideClick}
          style={contentStyle}
        >
          {searchable && (
            <>
              <FilterSearch
                value={value}
                searchPlaceholder={
                  filter.search?.placeholder ?? labels.searchPlaceholder
                }
                onChange={handleChange}
                onClear={onClear}
              />
              {(showResetButton || renderOptions) && (
                <div className={styles.separator} />
              )}
            </>
          )}
          {showResetButton && !isIncludeExcludeFilter && (
            <FilterResetButton handleReset={handleReset} />
          )}

          {renderOptions ? (
            renderOptions({
              options: data,
              showEmpty: Boolean(!data.length && value),
            })
          ) : (
            <div className={styles.scrollableList}>
              {multiselect && !!filterOptions.length && !value && (
                <MultiSelectedOptions
                  title={labels.selectedLabel}
                  options={filterOptions.filter(o => !o.childName)}
                  filterKey={filterKey}
                  onFilterSelect={onFilterSelect}
                  disabledTooltipLabel={labels.disabledTooltipLabel}
                  defaultOptionKey={config.default}
                  renderCustomFilterOptionLabel={
                    labels.renderCustomFilterOptionLabel
                  }
                />
              )}

              {(displayList || value) && <div className={styles.separator} />}
              {displayList && (
                <div className={styles.dropdownMenuTitle}>
                  {showListTitle && (
                    <p
                      className={styles.dropdownMenuTitleText}
                      data-testid="filter-title-text"
                    >
                      {listTitle}
                    </p>
                  )}
                  {config.children?.map(f => (
                    <ChildFilter
                      activeOptions={filterOptions}
                      filter={f}
                      key={f.name}
                      onClick={option =>
                        addFilterOption({
                          option,
                          filterKey,
                          filterId: id,
                        })
                      }
                    />
                  ))}
                </div>
              )}
              <div className={styles.scrollableList}>
                {showEmpty ? (
                  <p className={styles.noResults}>{labels.noResultsLabel}</p>
                ) : (
                  filteredOptions
                )}
              </div>
            </div>
          )}

          {variant === 'include-exclude-multiselect' && showResetButton && (
            <div className={styles.includeExcludeFooter}>
              <div className={styles.separator} />
              <div className={styles.includeExcludeFooterActions}>
                <FilterResetButton handleReset={handleReset} />
              </div>
            </div>
          )}

          {isSingleSelectIncludeExcludeFilter && isMultiUseSingleFilter && (
            <div className={styles.includeExcludeFooter}>
              <div className={styles.includeExcludeFooterActions}>
                <FilterFooterActions
                  filter={filter}
                  onRemove={handleRemove}
                  onCustomRangeClick={onCustomRangeClick}
                  isMultiUseSingleFilter={isMultiUseSingleFilter}
                  labels={labels}
                />
              </div>
            </div>
          )}

          {!isSingleSelectIncludeExcludeFilter && !value && (
            <FilterFooterActions
              filter={filter}
              onRemove={handleRemove}
              onCustomRangeClick={onCustomRangeClick}
              isMultiUseSingleFilter={isMultiUseSingleFilter}
              labels={labels}
            />
          )}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  );
};
